import { InAppState } from "../base/InAppState";
import { Grid, html } from "gridjs";

// Registers the <place-detail> custom element used by the template.
import "../../components/place/detail";

import template from "./PoiListTemplate.html";
import { _lcs } from "../../i18n";
import { gridLanguage } from "../../core/grid";

// The labels and titles below are German source strings that double as
// dictionary keys — module constants are evaluated before I18n exists, so the
// translation happens where they are read.
const STATUS_LABELS = {
    published : "Veröffentlicht",
    draft     : "Entwurf",
    archived  : "Archiviert"
};

/**
 * The category vocabulary of a point of interest.
 *
 * Held here rather than fetched, for the same reason the gastronomy list holds
 * its own: place categories carry no CHECK constraint and no catalog endpoint
 * — importers map provider vocabularies into the column freely (see
 * model/Place). This is the curated set the console offers as filters, grouped
 * the way an operator scans it.
 */
const CATEGORY_GROUPS = [
    {
        title : "Kultur & Geschichte",
        items : [
            { key: "sight",           title: "Sehenswertes",        icon: "landmark" },
            { key: "historic_site",   title: "Historische Stätte",  icon: "landmark" },
            { key: "historic_centre", title: "Historischer Kern",   icon: "home" },
            { key: "monument",        title: "Denkmal",             icon: "landmark" },
            { key: "castle",          title: "Burg & Schloss",      icon: "landmark" },
            { key: "ruin",            title: "Ruine",               icon: "landmark" },
            { key: "church",          title: "Kirche",              icon: "landmark" },
            { key: "monastery",       title: "Kloster",             icon: "landmark" },
            { key: "museum",          title: "Museum",              icon: "landmark" },
            { key: "gallery",         title: "Galerie",             icon: "image" },
            { key: "architecture",    title: "Architektur",         icon: "home" },
            { key: "mill",            title: "Mühle",               icon: "wind" }
        ]
    },
    {
        title : "Natur",
        items : [
            { key: "nature",      title: "Natursehenswürdigkeit", icon: "leaf" },
            { key: "viewpoint",   title: "Aussichtspunkt",        icon: "mountain" },
            { key: "park",        title: "Park & Garten",         icon: "flower-2" },
            { key: "lake",        title: "See",                   icon: "waves" },
            { key: "waterfall",   title: "Wasserfall",            icon: "droplets" },
            { key: "cave",        title: "Höhle",                 icon: "mountain" },
            { key: "geotope",     title: "Geotop",                icon: "mountain" },
            { key: "nature_info", title: "Naturinformation",      icon: "info" }
        ]
    },
    {
        title : "Freizeit",
        items : [
            { key: "playground",    title: "Spielplatz",       icon: "footprints" },
            { key: "swimming",      title: "Bad & Badestelle", icon: "waves" },
            { key: "wellness",      title: "Wellness & Sauna", icon: "heart" },
            { key: "zoo",           title: "Zoo & Tierpark",   icon: "paw-print" },
            { key: "theatre",       title: "Theater & Bühne",  icon: "ticket" },
            { key: "cinema",        title: "Kino",             icon: "tv" },
            { key: "culture_venue", title: "Veranstaltungsort", icon: "music" },
            { key: "club",          title: "Club",             icon: "music" },
            { key: "sports",        title: "Sport",            icon: "bike" },
            { key: "guided_tour",   title: "Führung",          icon: "compass" }
        ]
    },
    {
        title : "Service",
        items : [
            { key: "tourist_info", title: "Tourist-Information", icon: "info" },
            { key: "shop",         title: "Geschäft",            icon: "shop" },
            { key: "library",      title: "Bücherei",            icon: "book-open" },
            { key: "boat_rental",  title: "Bootsverleih",        icon: "ship" },
            { key: "transport",    title: "Verkehr",             icon: "bus-front" },
            { key: "parking",      title: "Parkmöglichkeit",     icon: "car" },
            { key: "toilet",       title: "WC",                  icon: "circle" },
            { key: "other",        title: "Sonstiges",           icon: "circle-help" }
        ]
    }
];

const CATEGORY_TITLES = CATEGORY_GROUPS
    .flatMap( ( group ) => group.items )
    .reduce( ( map, category ) =>
    {
        map[ category.key ] = category.title;
        return map;
    }, {} );

/**
 * PoiList — "Sehenswürdigkeiten": the platform's own pool of points of interest
 * (GET /place/platform?type=poi).
 *
 * The same screen as Gastronomie — same table, same filters, same detail panel
 * — over the other half of the shared place table. What differs is the
 * vocabulary: a POI has no price level and no kitchen, so those two columns
 * give way to "Draußen" (whether the sight is weather-dependent), and the
 * category filter is grouped, because a flat list of thirty-eight kinds is not
 * something anyone scans.
 */
export class PoiList extends InAppState
{
    static ROUTE = '/sights';
    static ID = 'poi-list';

    async enter()
    {
        this.app.view.render( this.app.view.pageContent, template );

        const root = this.app.view.pageContent;
        this.stateEl = root.querySelector( '[data-id="places-state"]' );
        this.truncatedEl = root.querySelector( '[data-id="truncated"]' );
        this.sourceFilter = root.querySelector( '[data-id="filter-source"]' );
        this.statusFilter = root.querySelector( '[data-id="filter-status"]' );
        this.duplicateFilter = root.querySelector( '[data-id="filter-duplicates"]' );
        this.detail = root.querySelector( '[data-id="place-detail"]' );

        this.categoryToggle = root.querySelector( '[data-id="filter-category-toggle"]' );
        this.categoryPanel = root.querySelector( '[data-id="filter-category-panel"]' );
        this.categoryList = root.querySelector( '[data-id="filter-category-list"]' );
        this.categoryLabel = root.querySelector( '[data-id="filter-category-label"]' );

        this.places = [];
        this.placeById = new Map();
        this.sources = [];
        this.selectedCategories = new Set();
        this.grid = null;

        // Handed over by the source list ("Sehenswürdigkeiten" on a source card).
        this.selectedSourceId = this.app.pendingPlaceSourceId || "";
        this.app.pendingPlaceSourceId = null;

        this.addEventListener( this.stateEl, "click", ( e ) => this.onGridAction( e ) );
        this.addEventListener( this.detail, "place-saved", () => this.loadPlaces() );
        this.addEventListener( this.sourceFilter, "change", () => this.loadPlaces() );
        this.addEventListener( this.statusFilter, "change", () => this.loadPlaces() );
        this.addEventListener( this.duplicateFilter, "change", () => this.loadPlaces() );
        this.addEventListener( root.querySelector( '[data-id="btn-reload"]' ), "click", () => this.loadPlaces() );

        this.bindCategoryFilter();
        this.renderCategoryFilter();

        await this.loadSources();
        await this.loadPlaces();
    }

    /* --------------------------- Category multi-select ------------------------ */

    bindCategoryFilter()
    {
        this.addEventListener( this.categoryToggle, "click", ( e ) =>
        {
            e.stopPropagation();
            this.toggleCategoryPanel( this.categoryPanel.hidden );
        } );

        this.addEventListener( this.categoryList, "change", ( e ) =>
        {
            const input = e.target.closest( "input[type=checkbox]" );
            if ( !input ) return;

            if ( input.checked ) this.selectedCategories.add( input.value );
            else this.selectedCategories.delete( input.value );

            this.updateCategoryLabel();
            this.loadPlaces();
        } );

        this.addEventListener( this.categoryPanel.querySelector( '[data-id="filter-category-clear"]' ), "click", () =>
        {
            this.selectedCategories.clear();
            this.categoryList.querySelectorAll( "input[type=checkbox]" ).forEach( ( input ) => { input.checked = false; } );
            this.updateCategoryLabel();
            this.loadPlaces();
        } );

        // Tracked, so InfrontJS removes them when the state exits.
        this.addEventListener( document, "click", ( e ) =>
        {
            if ( !this.categoryPanel.hidden && !this.categoryPanel.contains( e.target ) )
            {
                this.toggleCategoryPanel( false );
            }
        } );
        this.addEventListener( document, "keydown", ( e ) =>
        {
            if ( e.key === "Escape" ) this.toggleCategoryPanel( false );
        } );
    }

    toggleCategoryPanel( open )
    {
        this.categoryPanel.hidden = !open;
        this.categoryToggle.setAttribute( "aria-expanded", String( open ) );
    }

    renderCategoryFilter()
    {
        this.categoryList.innerHTML = CATEGORY_GROUPS.map( ( group ) =>
            '<div class="plc__multi-group">' + this._escape( _lcs( group.title ) ) + '</div>' +
            group.items.map( ( category ) =>
                '<label class="checkbox evp__multi-item">' +
                    '<input type="checkbox" value="' + this._escape( category.key ) + '" />' +
                    '<span class="checkbox__box"><i class="icon icon--check"></i></span>' +
                    '<i class="icon icon--' + this._escape( category.icon ) + ' evp__multi-icon"></i>' +
                    this._escape( _lcs( category.title ) ) +
                '</label>'
            ).join( "" )
        ).join( "" );

        this.updateCategoryLabel();
    }

    updateCategoryLabel()
    {
        const count = this.selectedCategories.size;

        if ( count === 0 )
        {
            this.categoryLabel.textContent = _lcs( "Alle Kategorien" );
        }
        else if ( count === 1 )
        {
            const key = Array.from( this.selectedCategories )[ 0 ];
            this.categoryLabel.textContent = CATEGORY_TITLES[ key ] ? _lcs( CATEGORY_TITLES[ key ] ) : key;
        }
        else
        {
            this.categoryLabel.textContent = _lcs( "{count} Kategorien", { count } );
        }

        this.categoryToggle.classList.toggle( "evp__multi-toggle--active", count > 0 );
    }

    /* ------------------------------- Loading -------------------------------- */

    /** Only POI feeds go in the filter. */
    async loadSources()
    {
        try
        {
            const { result } = await this.app.api.get( "/eventsource" );
            const all = ( result && +result.status === 200 && Array.isArray( result.data ) ) ? result.data : [];
            this.sources = all.filter( ( source ) => source.contentType === "poi" );
        }
        catch ( e )
        {
            this.sources = [];
        }

        this.sourceFilter.innerHTML = '<option value="">' + this._escape( _lcs( "Alle Quellen" ) ) + '</option>' +
            this.sources.map( ( source ) =>
                '<option value="' + this._escape( source.id ) + '">' + this._escape( source.name || source.key ) + '</option>'
            ).join( "" );

        this.sourceFilter.value = this.selectedSourceId || "";
    }

    async loadPlaces()
    {
        this.showMessage( "landmark", _lcs( "Lade Sehenswürdigkeiten …" ), _lcs( "Einen Moment bitte." ) );
        this.truncatedEl.hidden = true;

        const query = [ "type=poi" ];
        if ( this.sourceFilter.value ) query.push( "source=" + encodeURIComponent( this.sourceFilter.value ) );
        if ( this.statusFilter.value ) query.push( "status=" + encodeURIComponent( this.statusFilter.value ) );
        if ( this.selectedCategories.size > 0 )
        {
            query.push( "category=" + encodeURIComponent( Array.from( this.selectedCategories ).join( "," ) ) );
        }
        if ( this.duplicateFilter.value !== "exclude" ) query.push( "duplicates=" + encodeURIComponent( this.duplicateFilter.value ) );

        let payload = null;
        try
        {
            const { result } = await this.app.api.get( "/place/platform?" + query.join( "&" ) );
            if ( !result || +result.status !== 200 || !result.data )
            {
                throw new Error( "Unexpected /place/platform response." );
            }
            payload = result.data;
        }
        catch ( e )
        {
            console.error( e );
            this.showMessage(
                "triangle-alert",
                _lcs( "Sehenswürdigkeiten konnten nicht geladen werden" ),
                _lcs( "Bitte versuche es später erneut." )
            );
            return;
        }

        this.places = Array.isArray( payload.items ) ? payload.items : [];
        this.placeById = new Map();
        this.places.forEach( ( place ) => this.placeById.set( String( place.id ), place ) );

        // Never pretend a capped list is the whole list.
        if ( payload.isTruncated )
        {
            this.truncatedEl.textContent = _lcs(
                "Es werden die ersten {limit} Einträge angezeigt. Grenze die Auswahl über die Filter weiter ein.",
                { limit: payload.limit }
            );
            this.truncatedEl.hidden = false;
        }

        if ( this.places.length === 0 )
        {
            this.showMessage(
                "landmark",
                _lcs( "Keine Sehenswürdigkeiten gefunden" ),
                _lcs( "Für diese Auswahl liegen keine Daten vor. Der Import läuft über „Datenquellen“." )
            );
            return;
        }

        this.renderGrid();
    }

    renderGrid()
    {
        this._destroyGrid();
        this.stateEl.innerHTML = "";

        const rows = this.places.map( ( place ) => [
            place.name || "—",
            this._categoryText( place ),
            place.city || "—",
            this._outdoorText( place ),
            this._sourceLabel( place ),
            String( place.status || "" ),
            String( place.id )
        ] );

        this.grid = new Grid( {
            columns: [
                {
                    name: _lcs( "Name" ),
                    formatter: ( cell, row ) => html(
                        '<span class="admin-users__name" title="' + this._escape( cell ) + '">' +
                            this._markersHtml( row.cells[ 6 ] ? this.placeById.get( String( row.cells[ 6 ].data ) ) : null ) +
                            this._escape( cell ) +
                        '</span>'
                    )
                },
                {
                    name: _lcs( "Kategorie" ),
                    formatter: ( cell ) => html(
                        '<span class="admin-users__name" title="' + this._escape( cell ) + '">' + this._escape( cell ) + '</span>'
                    )
                },
                _lcs( "Ort" ),
                _lcs( "Draußen" ),
                _lcs( "Quelle" ),
                {
                    name: _lcs( "Status" ),
                    formatter: ( cell ) => html(
                        '<span class="acc-status acc-status--' + this._escape( String( cell ).toLowerCase() ) + '">' +
                            this._escape( STATUS_LABELS[ cell ] ? _lcs( STATUS_LABELS[ cell ] ) : ( cell || "—" ) ) +
                        '</span>'
                    )
                },
                {
                    name: _lcs( "Aktionen" ),
                    sort: false,
                    formatter: ( cell ) =>
                    {
                        const details = this._escape( _lcs( "Details" ) );
                        return html(
                            '<div class="admin-users__actions">' +
                                '<button type="button" class="admin-users__action" data-action="open" ' +
                                        'data-place-id="' + this._escape( cell ) + '" title="' + details + '" aria-label="' + details + '">' +
                                    '<i class="icon icon--eye"></i><span>' + details + '</span>' +
                                '</button>' +
                            '</div>'
                        );
                    }
                }
            ],
            data: rows,
            search: true,
            sort: true,
            pagination: { limit: 25, summary: true },
            className: { container: "admin-users__grid" },
            language: gridLanguage( _lcs( "Einträge" ), _lcs( "Keine Sehenswürdigkeiten gefunden" ) )
        } );

        this.grid.render( this.stateEl );
    }

    _markersHtml( place )
    {
        if ( !place )
        {
            return "";
        }

        let markers = "";
        if ( place.isLocked )
        {
            markers += '<i class="icon icon--lock evp__lock" title="' +
                this._escape( _lcs( "Manuell bearbeitet — wird beim Import nicht überschrieben" ) ) + '"></i> ';
        }
        if ( place.isDuplicate )
        {
            markers += '<i class="icon icon--layers evp__dupe" title="' +
                this._escape( _lcs( "Dublette — dieser Eintrag existiert bereits und wird Gästen nicht angezeigt" ) ) + '"></i> ';
        }
        if ( place.isPermanentlyClosed )
        {
            markers += '<i class="icon icon--x plc__closed" title="' +
                this._escape( _lcs( "Dauerhaft geschlossen" ) ) + '"></i> ';
        }
        return markers;
    }

    /* -------------------------------- Actions -------------------------------- */

    onGridAction( e )
    {
        const btn = e.target.closest( "[data-action]" );
        if ( !btn || !this.stateEl.contains( btn ) ) return;

        if ( btn.getAttribute( "data-action" ) === "open" )
        {
            this.openDetail( btn.getAttribute( "data-place-id" ) );
        }
    }

    async openDetail( placeId )
    {
        try
        {
            const { result } = await this.app.api.get( "/place/platform/" + placeId );
            if ( !result || +result.status !== 200 || !result.data )
            {
                throw new Error( "Unexpected place response." );
            }
            this.detail.open( result.data, this._sourceName( result.data.contentSourceId ) );
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( _lcs( "Eintrag konnte nicht geladen werden." ), "error" );
        }
    }

    /* ------------------------------- Formatting ------------------------------ */

    _categoryText( place )
    {
        const categories = Array.isArray( place.categories ) ? place.categories : [];
        if ( categories.length === 0 )
        {
            return "—";
        }
        return categories.map( ( key ) => CATEGORY_TITLES[ key ] ? _lcs( CATEGORY_TITLES[ key ] ) : key ).join( ", " );
    }

    /**
     * Weather-dependent? Derived by the importer from the categories, so it is
     * only ever true or unknown — never an explicit "indoor".
     */
    _outdoorText( place )
    {
        return place.isOutdoor === true ? _lcs( "Ja" ) : "—";
    }

    _sourceLabel( place )
    {
        if ( place.contentSourceId )
        {
            return this._sourceName( place.contentSourceId );
        }
        return place.source === "platform" ? "BELTURO" : ( place.sourceProvider || "—" );
    }

    _sourceName( contentSourceId )
    {
        const source = this.sources.find( ( s ) => String( s.id ) === String( contentSourceId ) );
        return source ? ( source.name || source.key ) : "—";
    }

    showMessage( icon, title, text )
    {
        this._destroyGrid();
        this.stateEl.innerHTML =
            '<div class="content__empty">' +
                '<span class="content__empty-icon"><i class="icon icon--' + icon + '"></i></span>' +
                '<h2></h2>' +
                '<p></p>' +
            '</div>';
        this.stateEl.querySelector( "h2" ).textContent = title;
        this.stateEl.querySelector( "p" ).textContent = text;
    }

    _destroyGrid()
    {
        if ( this.grid )
        {
            try { this.grid.destroy(); } catch ( e ) { /* DOM is replaced anyway */ }
            this.grid = null;
        }
    }

    async exit()
    {
        this._destroyGrid();
        await super.exit();
    }

    _escape( str )
    {
        return String( str === null || str === undefined ? "" : str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }

    getContentHeaderData()
    {
        return {
            title: _lcs( "Sehenswürdigkeiten" ),
            subtitle: _lcs( "Ausflugsziele, Aussichtspunkte und Museen der Region" )
        };
    }
}
